import { SVGAssets } from '@business/assets/vectors';
import { useTranslation } from 'react-i18next';
import { signOutHandler } from '@utils/helpers/lib/login';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { useUserDetailsByUserIdQuery } from '@ports/internal-services/userDetails/userDetailsSlice';
import { useEffect, useRef, useState } from 'react';
import {
  getFirstLogin,
  setPermissions,
  setIsAdmin,
  setFirstLogin
} from '@ports/common/authStateSlice';
import { useMeQuery } from '@ports/internal-services/user/userSlice';
import {
  setHaveSubscription,
  setSubscriberId
} from '@ports/common/sessionStateSlice';
import { setUserData } from '@ports/common/userStateSlice';
import {
  getSwitchedProfileUserId,
  setSwitchedProfileUserId
} from '@ports/common/commonStateSlice';
import { useGetMySpaceQuery } from '@ports/internal-services/invite/inviteSlice';
import { getRoleBadgeColor } from '@business/workflows/ComponentViews/Admin/adminBadgeUtils';
import { Avatar } from '../Avatar';
import { Button } from '../Button';
import { NotificationBell } from '../Notification';

export const Header = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isFirstLogin = useSelector(getFirstLogin);
  const switchedProfileUserId = useSelector(getSwitchedProfileUserId);
  const { data: userDetails, refetch } = useUserDetailsByUserIdQuery({} as any);
  const { data: mySpaceData, refetch: refetchMySpace } = useGetMySpaceQuery();

  const { data: loggedUserData, refetch: refetchMe } = useMeQuery(
    undefined,
    {}
  );

  const mySpaceProfiles = mySpaceData?.data ?? [];
  const [selectedUserId, setSelectedUserId] = useState<
    string | null | undefined
  >(undefined);
  const [isOwner, setIsOwner] = useState(false);

  /**
   * Store ORIGINAL logged-in user permanently.
   * This should NEVER change after first load.
   */
  const originalUserIdRef = useRef<string | null>(null);
  const originalSubscriberIdRef = useRef<string | null>(null);

  /**
   * Initial fetches
   */
  useEffect(() => {
    refetchMySpace();
    refetch();
    refetchMe();
  }, []);

  /**
   * Store original login values only once
   */
  useEffect(() => {
    if (loggedUserData?.data?.userId && !originalUserIdRef.current) {
      originalUserIdRef.current = loggedUserData.data.userId;
    }

    if (
      loggedUserData?.data?.subscriberId &&
      !originalSubscriberIdRef.current
    ) {
      originalSubscriberIdRef.current = loggedUserData.data.subscriberId;
    }
  }, [loggedUserData]);

  /**
   * Default selected profile
   */
  useEffect(() => {
    if (!selectedUserId && loggedUserData?.data?.userId) {
      setSelectedUserId(switchedProfileUserId ?? loggedUserData.data.userId);
    }
  }, [loggedUserData, switchedProfileUserId]);

  /**
   * Owner check
   */
  useEffect(() => {
    setIsOwner(selectedUserId === originalUserIdRef.current);
  }, [selectedUserId]);

  /**
   * Sync redux state (permissions and isAdmin only)
   */
  useEffect(() => {
    if (loggedUserData?.data?.permissions?.length > 0) {
      dispatch(
        setPermissions({
          permissions: loggedUserData?.data?.permissions || []
        })
      );
    }

    dispatch(
      setIsAdmin({
        isAdmin: loggedUserData?.data?.isAdmin || false
      })
    );

    if (typeof loggedUserData?.data?.haveSubscription === 'boolean') {
      dispatch(setHaveSubscription(loggedUserData?.data?.haveSubscription));
    }
  }, [loggedUserData, dispatch]);

  /**
   * Set initial user data only on first load
   */
  useEffect(() => {
    if (loggedUserData?.data && userDetails?.data && !switchedProfileUserId) {
      // Only set initial user data once for logged-in user
      const userData = {
        fullName: loggedUserData.data.fullName,
        email: loggedUserData.data.email,
        userId: loggedUserData.data.userId,
        profileImageUrl: userDetails.data.profileImage
      };
      dispatch(setUserData(userData));
    }
  }, [loggedUserData?.data?.userId, userDetails?.data?.profileImage]); // Only run when these specific values change

  /**
   * Correct profile switching logic
   */
  const handleSwitchProfile = async (
    profileSubscriberId?: string,
    userId?: string
  ) => {
    if (!userId) return;

    /**
     * Immediately update selected state
     * for instant UI response
     */
    setSelectedUserId(userId);

    /**
     * Determine whether selected profile
     * is original logged-in profile
     */
    const isOriginalProfile = userId === originalUserIdRef.current;

    /**
     * Update switched profile state
     */
    dispatch(setSwitchedProfileUserId(isOriginalProfile ? null : userId));

    /**
     * Find the profile being switched to
     */
    const targetProfile = mySpaceProfiles.find(p => p.userId === userId);

    try {
      /**
       * CASE 1:
       * Switching to another profile
       */
      if (profileSubscriberId) {
        dispatch(
          setSubscriberId({
            subscriberId: profileSubscriberId
          })
        );
        dispatch(setFirstLogin({ isFirstLogin: false }));

        /**
         * IMPORTANT:
         * Wait for next microtask
         * so Redux state updates first
         */
        await Promise.resolve();

        await refetchMe();
        const updatedUserDetails = await refetch();

        /**
         * Update user data in Redux with switched profile's data
         */
        if (isOriginalProfile) {
          // Switching back to original profile - use logged user data
          const userData = {
            fullName: loggedUserData?.data?.fullName,
            email: loggedUserData?.data?.email,
            userId: loggedUserData?.data?.userId,
            profileImageUrl: updatedUserDetails?.data?.data?.profileImage
          };
          dispatch(setUserData(userData));
        } else if (targetProfile) {
          // Switching to another profile - use target profile data
          const userData = {
            fullName: targetProfile.name,
            email: targetProfile.email,
            userId: targetProfile.userId,
            profileImageUrl: targetProfile.profileImage
          };
          dispatch(setUserData(userData));
        }

        /**
         * Navigate to Photos when subscriberId exists
         */
        // if already on photos page, just refetch data without navigating
        if (window.location.pathname === routePaths.Photos) {
          window.location.reload();
        } else {
          navigate(routePaths.Photos);
        }
      } else {
        /**
         * CASE 2:
         * No subscriberId
         * Clear subscriberId first
         */
        dispatch(
          setSubscriberId({
            subscriberId: null
          })
        );

        await Promise.resolve();

        await refetchMe();
        const updatedUserDetails = await refetch();

        /**
         * Update user data in Redux with switched profile's data
         */
        if (isOriginalProfile) {
          // Switching back to original profile - use logged user data
          const userData = {
            fullName: loggedUserData?.data?.fullName,
            email: loggedUserData?.data?.email,
            userId: loggedUserData?.data?.userId,
            profileImageUrl: updatedUserDetails?.data?.data?.profileImage
          };
          dispatch(setUserData(userData));
        } else if (targetProfile) {
          // Switching to another profile - use target profile data
          const userData = {
            fullName: targetProfile.name,
            email: targetProfile.email,
            userId: targetProfile.userId,
            profileImageUrl: targetProfile.profileImage
          };
          dispatch(setUserData(userData));
        }

        navigate(routePaths.Sharing);
      }
    } catch (error) {
      console.error('Profile switch failed:', error);
    }
  };

  const ownFullName =
    userDetails?.data?.firstName ?? loggedUserData?.data?.fullName;

  // Derive the currently active profile entry from mySpaceProfiles
  const activeProfile = mySpaceProfiles.find(p => p.userId === selectedUserId);

  const isOwnProfileActive =
    !activeProfile || activeProfile.userId === originalUserIdRef.current;

  // Display values switch to the selected profile's data when a non-own profile is active
  const displayName = activeProfile?.name ?? ownFullName;

  const displayImage =
    activeProfile?.profileImage ?? userDetails?.data?.profileImage;

  const displayEmail = isOwnProfileActive
    ? loggedUserData?.data?.email
    : (activeProfile?.email ?? loggedUserData?.data?.email);

  const userRole = loggedUserData?.data?.roleName || '-';

  const dropdownItems = [
    {
      label: t('logout'),
      onClick: () => signOutHandler(dispatch, navigate),
      decorator: <SVGAssets.LogoutIcon className="w-5 h-5 mr-2" />
    }
  ];

  const isAdmin = loggedUserData?.data?.isAdmin || false;

  return (
    <header className="flex-shrink-0 bg-white shadow-sm z-50">
      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex items-center justify-between h-20">
          <div className="flex items-center gap-4 ml-12 md:ml-0">
            <SVGAssets.HeaderLogo className="h-12" />
          </div>

          <div className="flex items-center gap-2">
            <NotificationBell />
            <div className="relative group">
              <div className="flex items-center cursor-pointer">
                <Avatar
                  alt={`${displayName}`}
                  className="w-10 h-10 overflow-hidden rounded-full"
                  src={displayImage}
                  isHeader={true}
                />

                <SVGAssets.PricingArrow className="w-2 -rotate-90 ml-2" />
              </div>

              <ul
                className="hidden group-hover:block absolute right-0 z-50 shadow-lg bg-white rounded-lg w-82 py-2 border border-gray-100 hover:block"
                id="sideNav">
                <li className="flex items-center gap-3 p-3">
                  <Avatar
                    alt={`${displayName}`}
                    className="w-20 h-20 overflow-hidden rounded-full"
                    src={displayImage}
                    isHeader={true}
                  />

                  <div className="flex flex-col">
                    <div className="flex items-center justify-between gap-2 w-full">
                      <div className="flex items-center gap-1">
                        <span className="text-[15px] font-semibold text-[#3F602B]">
                          {displayName}
                        </span>

                        {!isFirstLogin && isOwner && (
                          <Button
                            btnStyle="link"
                            href={routePaths.EditProfile}
                            className="p-0">
                            <SVGAssets.ProfileEditIcon className="w-4 h-4 text-[#3F602B]" />
                          </Button>
                        )}
                      </div>

                      {isAdmin && (
                        <span
                          className={`text-xs font-medium ${getRoleBadgeColor(
                            userRole
                          )} px-2 py-1 rounded-full w-full capitalize`}>
                          {userRole}
                        </span>
                      )}
                    </div>

                    <span className="text-xs text-[#8D8D8D] w-50 w-full">
                      {displayEmail}
                    </span>

                    {!isFirstLogin && isOwner && (
                      <Button
                        btnStyle="link"
                        href={routePaths.MyProfile}
                        className="text-[13px] text-[#6B6B6B] mt-1 hover:bg-[#ebe5e5] px-1 py-2 rounded-md">
                        {t('accountSettings')}
                      </Button>
                    )}
                  </div>
                </li>

                {mySpaceProfiles.length > 1 && (
                  <>
                    <li className="bg-[#F0F7E4] px-4 py-2">
                      <span className="text-[#3F602B] font-semibold text-sm">
                        {t('switchProfile')}
                      </span>
                    </li>

                    {mySpaceProfiles.map(profile => {
                      const isActive = profile.userId === selectedUserId;

                      const isOwnProfile =
                        profile.userId === originalUserIdRef.current;

                      const profileDisplayName = isOwnProfile
                        ? `You (${profile.name})`
                        : profile.name;

                      return (
                        <li
                          key={profile.subscriberId ?? profile.userId}
                          className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                          onClick={() =>
                            handleSwitchProfile(
                              profile.subscriberId,
                              profile.userId
                            )
                          }>
                          <Avatar
                            alt={`${profile.name}`}
                            className="w-10 h-10 overflow-hidden rounded-full flex-shrink-0"
                            src={profile.profileImage}
                            isHeader={false}
                          />

                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="text-sm font-semibold text-gray-800 truncate">
                              {profileDisplayName}
                            </span>

                            <span className="text-xs text-gray-500 truncate">
                              {profile.roleName}
                            </span>
                          </div>

                          <div className="flex-shrink-0 ml-2">
                            {isActive ? (
                              <div className="w-6 h-6 rounded-full bg-[#3F602B] flex items-center justify-center">
                                <svg
                                  className="w-3.5 h-3.5 text-white"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                  strokeWidth={3}>
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </>
                )}

                {dropdownItems.map((item, index) => (
                  <li
                    key={index}
                    className="w-full flex items-center bg-[#F9FEE9] px-4 py-2 hover:bg-[#ebe5e5]">
                    <span>{item.decorator}</span>

                    <Button
                      btnStyle="link"
                      className="text-[#49712B]font-normal text-sm py-2 px-4 w-full text-left"
                      onClick={item.onClick}>
                      {item.label}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
